import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ChevronLeftIcon } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { AssetPosition } from "$/modules/appwrite/types/collections";
import { QuestionMedia } from "$/modules/question/ui/question-media";
import { sortQuestionsByStoredOrder } from "$/modules/questionnaire/lib/question-order";
import { GameShowAnswerFeedback } from "$/modules/quiz-player/game-show/ui/game-show-answer-feedback";
import { cn } from "$/modules/shared/lib/utils";
import { useQuizPlayer } from "$/modules/shared/provider/quiz-player-provider";
import { Button } from "$/modules/shared/ui/button";
import { Field, FieldLabel } from "$/modules/shared/ui/field";
import { Input } from "$/modules/shared/ui/input";

const normalize = (value: string) => value.trim().toLowerCase();

const answerColorClasses = [
	"bg-[#dc2626] text-white",
	"bg-[#15803d] text-white",
	"bg-[#2563eb] text-white",
	"bg-[#facc15] text-[#111827]",
];

export const Route = createFileRoute(
	"/$locale/quiz-player/$projectId/questionnaires/$questionnaireId/$questionId/",
)({
	component: QuestionPage,
});

function QuestionPage() {
	const fieldId = useId();

	const t = useTranslations("quiz");
	const { questions, questionnaires, setQuestions } = useQuizPlayer();
	const router = useRouter();

	const { questionId, questionnaireId } = Route.useParams();

	const question = useMemo(
		() => questions.find((q) => q.$id === questionId),
		[questions, questionId],
	);

	const [selected, setSelected] = useState<string[]>([]);
	const [freeTextAnswer, setFreeTextAnswer] = useState("");
	const [pickedAll, setPickedAll] = useState(false);

	const isTextQuestion = question?.questionType === "Text";
	const isInfoQuestion = question?.questionType === "Info";
	const questionnaire = questionnaires.find(
		(item) => item.$id === questionnaireId,
	);
	const questionIndex = questionnaire
		? sortQuestionsByStoredOrder(
				questionnaire.$id,
				questionnaire.questions ?? [],
			).findIndex((item) => item.$id === questionId)
		: -1;
	const points = (questionIndex + 1) * 100;

	const isCorrect = useMemo(() => {
		if (!question) return null;

		if (question.questionType === "Text") {
			if (!pickedAll) return null;
			return question.correctOption.some(
				(option) => normalize(option) === normalize(freeTextAnswer),
			);
		}

		if (!pickedAll || selected.length < question.correctOption.length)
			return null;

		const sortedOptions = [...question.correctOption].sort();
		const selectedOptions = [...selected].sort();

		return sortedOptions.every((val, index) => val === selectedOptions[index]);
	}, [question, selected, freeTextAnswer, pickedAll]);

	if (!question) {
		toast.error(t("questionnaireNotFound"), {});
		router.history.back();
		return;
	}

	const finishQuestion = () => {
		setQuestions((previous) =>
			previous.map((item) => ({
				...item,
				done: item.$id === questionId ? true : item.done,
			})),
		);
		router.history.back();
	};

	return (
		<div className="min-h-[100dvh] bg-content2/70 p-3 sm:p-5">
			<div className="mx-auto flex min-h-[calc(100dvh-1.5rem)] w-full max-w-[1600px] flex-col sm:min-h-[calc(100dvh-2.5rem)]">
				<header className="mb-3 flex items-center justify-between gap-3 sm:mb-5">
					<Button
						aria-label={t("backToOverview")}
						onClick={() => router.history.back()}
						variant="ghost"
						size="icon"
						type="button"
					>
						<ChevronLeftIcon className="size-5" />
					</Button>
					<div className="text-center">
						<p className="text-xs font-extrabold uppercase tracking-[0.18em] text-primary">
							{questionnaire?.name ?? t("gameShow")}
						</p>
						<p className="text-sm font-medium text-foreground-500">
							{t("questionProgress", {
								questionProgress: questionIndex + 1,
								totalQuestions: questionnaire?.questions?.length ?? 0,
							})}
						</p>
					</div>
					<div className="min-w-12 rounded-xl bg-primary px-3 py-2 text-center text-primary-foreground">
						<p className="text-[10px] font-extrabold uppercase tracking-wider opacity-75">
							{t("points")}
						</p>
						<p className="font-display text-xl font-bold leading-none">
							{points}
						</p>
					</div>
				</header>

				<section className="flex flex-1 flex-col overflow-hidden rounded-[1.75rem] border border-default-200 bg-content1 sm:rounded-[2rem]">
					<div className="flex flex-1 flex-col justify-center gap-6 px-5 py-7 sm:px-10 sm:py-9 lg:gap-8 lg:px-14">
						<QuestionMedia
							question={question}
							position={AssetPosition.BeforeQuestion}
						/>
						<div className="text-center">
							<h1 className="text-balance font-display text-3xl font-bold leading-tight tracking-tight sm:text-5xl xl:text-6xl">
								{question.question}
							</h1>
						</div>

						<QuestionMedia
							question={question}
							position={AssetPosition.AfterQuestion}
						/>
					</div>
					<div className="border-t border-default-100 bg-content2/40 px-5 py-5 sm:px-10 sm:py-7">
						{isInfoQuestion ? (
							<div className="rounded-2xl bg-primary/5 p-5 text-center text-foreground-600" />
						) : isTextQuestion ? (
							<div className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row">
								<Field data-disabled={pickedAll}>
									<FieldLabel htmlFor={`${fieldId}-100`}>
										{t("yourAnswer")}
									</FieldLabel>
									<Input
										id={`${fieldId}-100`}
										value={freeTextAnswer}
										disabled={pickedAll}
										onChange={(event) =>
											setFreeTextAnswer(event.currentTarget.value)
										}
									/>
								</Field>
								<Button
									className="w-full sm:w-auto"
									onClick={() => setPickedAll(true)}
									disabled={pickedAll || freeTextAnswer.trim().length === 0}
									variant="default"
									type="button"
								>
									{t("checkAnswer")}
								</Button>
							</div>
						) : (
							<div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
								{question.options.map((option, index) => {
									return (
										<Button
											key={option}
											size="lg"
											className={cn(
												"h-auto min-h-20 whitespace-normal rounded-2xl border border-transparent p-4 text-center text-lg font-bold shadow-none transition-colors hover:border-primary/25 sm:min-h-28 lg:min-h-32 lg:text-xl",
												answerColorClasses[index % answerColorClasses.length],
											)}
											onClick={() =>
												setSelected((previous) => {
													const next = [...previous, option];
													if (next.length >= question.correctOption.length)
														setPickedAll(true);
													return next;
												})
											}
											disabled={pickedAll || selected.includes(option)}
											variant="muted"
											type="button"
										>
											{option}
										</Button>
									);
								})}
							</div>
						)}

						{isInfoQuestion && (
							<div className="mt-6 text-center">
								<Button
									className="mt-4 w-full sm:w-auto"
									onClick={finishQuestion}
									variant="muted"
									type="button"
								>
									{<ChevronLeftIcon />}
									{t("backToOverview")}
								</Button>
							</div>
						)}
					</div>
				</section>
				{pickedAll && !isInfoQuestion && (
					<GameShowAnswerFeedback
						key={questionId}
						success={isCorrect === true}
						correctOptions={question.correctOption}
						explanation={question.explanation}
						onContinue={finishQuestion}
					/>
				)}
			</div>
		</div>
	);
}
